/**
 * Turkish UI strings, keyed by the English text (lib/i18n.ts). Trading terms that Turkish
 * traders use as-is (PnL, RVOL, ATR, spread, stop, take-profit) are kept in English.
 * `i18n.test.ts` fails when a `t('…')` literal or an enum value shown in the UI has no entry.
 */
export const TR: Readonly<Record<string, string>> = {
  // ── Navigation & shell ──
  Dashboard: 'Panel',
  'Trades & signals': 'İşlemler ve sinyaller',
  Strategies: 'Stratejiler',
  'Risk & universe': 'Risk ve coin listesi',
  'News sources': 'Haber kaynakları',
  Sentiment: 'Duygu analizi',
  Credentials: 'API anahtarları',
  Settings: 'Ayarlar',
  Audit: 'Denetim kaydı',
  'Sign out': 'Çıkış yap',
  Password: 'Şifre',
  'Sign in': 'Giriş yap',
  'Signing in…': 'Giriş yapılıyor…',

  // ── Engine ──
  Engine: 'Motor',
  starting: 'başlıyor',
  running: 'çalışıyor',
  paused: 'duraklatıldı',
  halted: 'durduruldu',
  panic: 'panik',
  'LIVE — real money': 'CANLI — gerçek para',
  PAPER: 'SİMÜLASYON',
  connected: 'bağlı',
  connecting: 'bağlanıyor',
  closed: 'kapalı',
  open: 'açık',
  Pause: 'Duraklat',
  Resume: 'Devam ettir',
  'Reset breaker': 'Devre kesiciyi sıfırla',
  PANIC: 'PANİK',
  'Engine paused — exits keep running': 'Motor duraklatıldı — çıkış yönetimi çalışmaya devam ediyor',
  'Engine running': 'Motor çalışıyor',
  'Circuit breaker reset': 'Devre kesici sıfırlandı',
  'Liquidate everything?': 'Her şey satılsın mı?',
  Continue: 'Devam',
  'Every open position ({n}) is market-sold to USDT and the engine halts. It will not resume on its own.':
    'Açık pozisyonların tamamı ({n}) piyasa fiyatından USDT’ye satılır ve motor durur. Kendiliğinden yeniden başlamaz.',
  'Final confirmation': 'Son onay',
  'Liquidate now': 'Şimdi sat',
  'This cannot be undone.': 'Bu işlem geri alınamaz.',
  'Panic: {n} position(s) not closed — check Positions': 'Panik: {n} pozisyon kapatılamadı — pozisyonları kontrol et',
  'Panic complete — {n} position(s) closed, engine {state}': 'Panik tamamlandı — {n} pozisyon kapatıldı, motor {state}',
  'Type the phrase to confirm:': 'Onaylamak için şu ifadeyi yaz:',
  'Go live…': 'Canlıya geç…',
  'Back to paper': 'Simülasyona dön',
  'Switch to LIVE trading?': 'CANLI işleme geçilsin mi?',
  'From now on orders use real money on Binance. Before continuing:': 'Bundan sonra emirler Binance’te gerçek parayla verilir. Devam etmeden önce:',
  'Binance key: spot trading only, withdrawals disabled, IP-restricted — and verified on the Credentials page':
    'Binance anahtarı: sadece spot işlem izni, para çekme kapalı, IP kısıtlı — ve API anahtarları sayfasında doğrulanmış',
  'The engine is paused and no paper positions are open': 'Motor duraklatılmış ve açık simülasyon pozisyonu yok',
  'Risk limits (position size, daily drawdown) are set deliberately; start with a small balance':
    'Risk limitleri (pozisyon büyüklüğü, günlük düşüş) bilinçli ayarlanmış; küçük bir bakiyeyle başla',
  'Go live': 'Canlıya geç',
  'The engine stays paused after the switch; nothing trades until you resume it.': 'Geçişten sonra motor duraklatılmış kalır; sen devam ettirene kadar işlem açılmaz.',
  'Back to paper mode?': 'Simülasyon moduna dönülsün mü?',
  'Switch to paper': 'Simülasyona geç',
  'Pause the engine and close live positions first; the switch is refused otherwise.': 'Önce motoru duraklat ve canlı pozisyonları kapat; aksi halde geçiş reddedilir.',
  'LIVE mode on — the engine is paused; resume when ready': 'CANLI mod açık — motor duraklatılmış; hazır olunca devam ettir',
  'Back to paper mode': 'Simülasyon moduna dönüldü',
  Cancel: 'Vazgeç',
  'Working…': 'İşleniyor…',

  // ── Dashboard ──
  'Equity (USDT)': 'Varlık (USDT)',
  Today: 'Bugün',
  'Drawdown today / limit': 'Bugünkü düşüş / limit',
  'Breaker tripped — entries blocked': 'Devre kesici devrede — yeni alımlar engellendi',
  Strategy: 'Strateji',
  'none active': 'aktif strateji yok',
  Uptime: 'Çalışma süresi',
  'Time range': 'Zaman aralığı',
  '7d': '7g',
  '30d': '30g',
  '90d': '90g',
  all: 'tümü',
  Equity: 'Varlık',
  'Realized PnL per day': 'Günlük gerçekleşen PnL',
  Total: 'Toplam',
  'Win rate': 'Kazanma oranı',
  'Profit factor': 'Kâr faktörü',
  'Max DD': 'En büyük düşüş',
  'Open positions ({n})': 'Açık pozisyonlar ({n})',
  'Live feed': 'Canlı akış',
  'Waiting for events…': 'Olaylar bekleniyor…',
  'No equity snapshots yet.': 'Henüz varlık kaydı yok.',
  'No closed trades in this range.': 'Bu aralıkta kapanmış işlem yok.',
  Chart: 'Grafik',
  Table: 'Tablo',
  Time: 'Zaman',
  'Equity over time in USDT': 'Zaman içinde varlık (USDT)',
  'Realized PnL per day, gains blue above zero, losses red below':
    'Günlük gerçekleşen PnL; kazançlar sıfırın üstünde mavi, kayıplar altında kırmızı',
  '{trades} trades · {wins} wins': '{trades} işlem · {wins} kazançlı',

  // ── Live feed messages ──
  'Opened {symbol} × {qty} @ {price}': '{symbol} açıldı × {qty} @ {price}',
  'Closed {symbol} ({reason}) {pnl} USDT': '{symbol} kapandı ({reason}) {pnl} USDT',
  '{strategy} → {symbol} (confidence {conf})': '{strategy} → {symbol} (güven {conf})',
  'Signal rejected: {reason}': 'Sinyal reddedildi: {reason}',
  'Veto cleared: {symbol}': 'Veto kaldırıldı: {symbol}',
  'VETO {symbol}: {title}': 'VETO {symbol}: {title}',
  'Circuit breaker TRIPPED at {dd}% (limit {limit}%)': 'Devre kesici DEVREDE: %{dd} (limit %{limit})',

  // ── Positions & trades ──
  'No open positions.': 'Açık pozisyon yok.',
  Symbol: 'Sembol',
  Qty: 'Miktar',
  Entry: 'Giriş',
  Last: 'Son',
  Stop: 'Stop',
  Exit: 'Çıkış',
  Opened: 'Açılış',
  Close: 'Kapat',
  'Close {symbol}?': '{symbol} kapatılsın mı?',
  'Market close': 'Piyasadan kapat',
  'Sells {qty} {asset} at market.': '{qty} {asset} piyasa fiyatından satılır.',
  // ── Open position cards & chart ──
  'Hide chart': 'Grafiği gizle',
  '{qty} {asset} @ {price}': '{qty} {asset} @ {price}',
  'Worth now': 'Şu anki değeri',
  'price {price}': 'fiyat {price}',
  'Net result (after fees)': 'Net kâr/zarar (komisyon sonrası)',
  'Binance commission': 'Binance komisyonu',
  paid: 'ödendi',
  '+ {fee} on sale (est.)': '+ satışta ~{fee}',
  'Break-even price': 'Başa baş fiyatı',
  'sell above this to profit': 'bunun üstünde satış kârlıdır',
  'target {price}': 'hedef {price}',
  'no fixed target': 'sabit hedef yok',
  'Sell at a loss': 'Zararına sat',
  'Sell with profit': 'Kârla sat',
  'Estimated net result, after both commissions:': 'Alış ve satış komisyonları düşülmüş tahmini net sonuç:',
  'The final price can differ slightly from the one shown.': 'Gerçekleşen fiyat gösterilenden biraz farklı olabilir.',
  BUY: 'AL',
  'Break-even': 'Başa baş',
  Target: 'Hedef',
  'Open in TradingView ↗': "TradingView'da aç ↗",
  'All strategies': 'Tüm stratejiler',
  'Which strategy did better': 'Hangi strateji daha iyi',
  // ── Trend follower ──
  'Only for the trend follower': 'Yalnızca Trend Takipçisi için',
  signal_exit: 'strateji satışı',
  trend_follower: 'Trend Takipçisi',
  trend_hold: 'Trend tutma (acil stop)',
  'Universe size': 'Coin sayısı',
  'Min median volume usdt': 'En düşük günlük hacim (USDT)',
  'Max coin pct': 'Coin başına en fazla (%)',
  'Target vol pct': 'Hedef oynaklık (%)',
  'Vol lookback days': 'Oynaklık ölçüm süresi (gün)',
  'Btc filter': 'BTC filtresi',
  'Entry signal': 'Alım eşiği',
  'Exit signal': 'Satış eşiği',
  'Holds for days': 'Günlerce tutar',
  'Used automatically by the trend follower: the strategy itself decides when to sell. This only adds an emergency sale if the price falls a set % below the buy price.': 'Trend Takipçisi otomatik olarak kullanır: ne zaman satılacağına strateji kendisi karar verir. Bu çıkış yalnızca fiyat alış fiyatının belirli bir % altına düşerse acil satış ekler.',
  'How many coins it may hold: the most traded ones, by typical daily volume.': 'Kaç coin tutabileceği: günlük hacmi en yüksek olanlar arasından.',
  'Skip coins that trade less than this per day (USDT). Thin markets are easy to manipulate.': 'Günlük hacmi bunun altındaki coinleri atla (USDT). Sığ piyasalar kolay manipüle edilir.',
  'Largest share of the account for one coin, in %. Coins that swing more get less. The risk page’s max position also applies.': 'Bir coine ayrılabilecek en büyük pay (%). Daha çok dalgalanan coinlere daha az ayrılır. Risk sayfasındaki en büyük pozisyon sınırı da geçerlidir.',
  'How wild a coin may be before its position is made smaller (yearly swing, in %).': 'Pozisyonun küçültülmesinden önce bir coinin ne kadar dalgalı olabileceği (yıllık dalgalanma, %).',
  'How many days are used to measure how wild a coin is.': 'Bir coinin ne kadar dalgalı olduğunu ölçmek için kaç günlük veri kullanılacağı.',
  'Do not buy anything new while Bitcoin is below its 200-day average — the whole market is usually weak then.': 'Bitcoin 200 günlük ortalamasının altındayken yeni alım yapma — o dönemlerde genelde tüm piyasa zayıftır.',
  'Share of the eight trend signals that must be on to buy (0.5 = half).': 'Alım için sekiz trend sinyalinden ne kadarının açık olması gerektiği (0,5 = yarısı).',
  'Sell when fewer than this share of the signals are still on (0.25 = a quarter).': 'Açık kalan sinyallerin payı bunun altına düşünce sat (0,25 = dörtte biri).',
  'Buys coins that are climbing to new highs and holds them for days or weeks while the rise lasts. The strategy with the strongest research behind it.': 'Yeni zirvelere tırmanan coinleri alır ve yükseliş sürdükçe günlerce, haftalarca tutar. Arkasında en güçlü akademik araştırma olan strateji.',
  'Long rising markets. Tested on 2022–2026 data it earned about twice as much as simply holding Bitcoin, with smaller falls.': 'Uzun süren yükseliş dönemleri. 2022–2026 verileriyle yapılan testte sadece Bitcoin tutmanın yaklaşık iki katını kazandırdı, düşüşleri de daha küçüktü.',
  'It buys late and sells late: it gives back part of the gains when a trend ends, and in sideways markets it loses small amounts repeatedly. Expect losing months.': 'Geç alır, geç satar: trend bitince kazancın bir kısmını geri verir, yatay piyasada tekrar tekrar küçük zararlar eder. Zararlı aylar olacağını bekle.',
  'Once a day, right after midnight (UTC), it looks at the daily chart of the most traded coins.': 'Günde bir kez, gece yarısından (UTC, Türkiye saatiyle 03:00) hemen sonra en çok işlem gören coinlerin günlük grafiğine bakar.',
  'For each coin it asks eight questions: is the price above its highest point of the last 10, 20, 30… up to 360 days? When at least half say yes, the coin is trending.': 'Her coin için sekiz soru sorar: fiyat son 10, 20, 30… 360 günün en yüksek seviyesini geçti mi? En az yarısına evet cevabı çıkarsa coin trenddedir.',
  'It sells when most of the eight signals turn off. It does not react to daily ups and downs; only a 25% crash triggers an emergency sale.': "Sekiz sinyalin çoğu kapanınca satar. Günlük iniş çıkışlara tepki vermez; yalnızca %25'lik bir çöküş acil satışı tetikler.",
  'Capital share saved': 'Sermaye payı kaydedildi',
  'Capital share (%)': 'Sermaye payı (%)',
  'no limit': 'sınırsız',
  'The most of the account this strategy may have invested at once. It never uses another strategy’s share.': 'Bu stratejinin aynı anda yatırımda tutabileceği en fazla hesap payı. Başka bir stratejinin payını asla kullanmaz.',
  'Max positions': 'En fazla pozisyon',
  Rotation: 'Değiştirme',
  'Rotate min signal': 'Değiştirme için gereken güç',
  'Rotate below signal': 'Değiştirilecek coinin güç sınırı',
  'Min hold days': 'En az tutma süresi (gün)',
  'At most this many coins at once. The strongest trends get the places.': 'Aynı anda en fazla bu kadar coin. Yerler en güçlü trendlere verilir.',
  'When every place is taken, sell the weakest coin to buy a full-strength trend. Tested: slightly better, about 10 more trades over 4.7 years.': 'Tüm yerler doluyken, tam güçteki bir trendi almak için en zayıf coini sat. Testte biraz daha iyi sonuç verdi; 4,7 yılda yaklaşık 10 işlem fazla.',
  'How strong an outside coin must be to replace a held one (1 = all eight signals on).': 'Dışarıdaki bir coinin, elimizdekinin yerine geçmesi için ne kadar güçlü olması gerektiği (1 = sekiz sinyalin hepsi açık).',
  'Only a held coin weaker than this can be replaced (0.5 = fewer than half the signals on).': 'Yalnızca gücü bunun altında olan bir coin değiştirilebilir (0,5 = sinyallerin yarısından azı açık).',
  'A coin must be held at least this many days before it can be replaced — no back-and-forth.': 'Bir coin değiştirilmeden önce en az bu kadar gün tutulmalı — git-gel yapılmasın.',
  'It holds the 5 strongest trends. The stronger a coin’s trend, the more money it gets — coins that swing wildly get less. Nothing new is bought while Bitcoin itself is in a downtrend.': 'En güçlü 5 trendi tutar. Bir coinin trendi ne kadar güçlüyse ona o kadar çok para ayrılır — çok dalgalı coinlere daha az. Bitcoin düşüş trendindeyken yeni alım yapılmaz.',
  'Every day it resizes: it buys more of a coin whose trend got stronger and sells part of one that weakened. Changes under 25% are skipped to save fees.': "Her gün pozisyonları yeniden boyutlandırır: trendi güçlenen coinden biraz daha alır, zayıflayanın bir kısmını satar. Komisyondan tasarruf için %25'ten küçük değişiklikler atlanır.",
  'A coin that falls out of the top 5 or loses its trend is sold. Daily ups and downs alone never trigger a sale; only a 25% crash triggers an emergency sale.': "İlk 5'ten düşen ya da trendini kaybeden coin satılır. Günlük iniş çıkışlar tek başına satış yaptırmaz; yalnızca %25'lik bir çöküş acil satışı tetikler.",
  'continuous: every day each position follows its trend strength (best in tests). binary: buy the full amount once, hold, sell when the trend ends.': 'continuous (sürekli): her gün her pozisyon trend gücünü izler (testlerde en iyisi). binary (sade): tam tutarı bir kez al, tut, trend bitince sat.',
  'Skip resizing when the change is smaller than this share of the position, in % — fewer small trades, lower fees.': 'Değişiklik pozisyonun bu yüzdesinden küçükse yeniden boyutlandırma yapma — daha az küçük işlem, daha düşük komisyon.',
  Mode: 'Mod',
  'Rebalance threshold pct': 'Yeniden boyutlandırma eşiği (%)',
  rebalance: 'yeniden boyutlandırma',
  // ── Backtest ──
  'Not enough data for a chart.': 'Grafik için yeterli veri yok.',
  'Holding BTC instead': 'Yerine BTC tutsaydık',
  'Strategy equity compared with holding BTC': 'Strateji bakiyesi ile BTC tutmanın karşılaştırması',
  Backtest: 'Geriye dönük test',
  'A backtest replays the past through a strategy: what would it have earned, after Binance fees, compared with simply holding BTC? It never places orders.': 'Geriye dönük test, geçmişi bir strateji üzerinden yeniden oynatır: Binance komisyonları düşüldükten sonra ne kazandırırdı, sadece BTC tutmakla karşılaştırınca nasıl? Hiçbir emir göndermez.',
  Coins: 'Coin sayısı',
  'The most traded USDT pairs.': 'En çok işlem gören USDT pariteleri.',
  'Starting capital (USDT)': 'Başlangıç sermayesi (USDT)',
  'Binance fee per trade (%)': 'İşlem başına Binance komisyonu (%)',
  '0.1 normally, 0.075 when paying fees in BNB.': 'Normalde 0,1; komisyonu BNB ile öderken 0,075.',
  'Exit strategy': 'Çıkış stratejisi',
  'The one in use': 'Kullanımdaki',
  'Max per coin (%)': 'Coin başına en fazla (%)',
  'Target volatility (%)': 'Hedef oynaklık (%)',
  'More volatile coins get a smaller position.': 'Daha oynak coinlere daha küçük pozisyon açılır.',
  'Minimum daily volume (USDT)': 'En düşük günlük hacim (USDT)',
  'No new buys while BTC is below its 200-day average': 'BTC 200 günlük ortalamasının altındayken yeni alım yapma',
  'Run backtest': 'Testi çalıştır',
  'Previous backtests': 'Önceki testler',
  'No backtests yet.': 'Henüz test yok.',
  BTC: 'BTC',
  'Backtest failed': 'Test başarısız',
  'Running… downloading history and replaying the strategy. This can take a few minutes.': 'Çalışıyor… geçmiş veriler indiriliyor ve strateji yeniden oynatılıyor. Birkaç dakika sürebilir.',
  'No history for {symbol}; left out.': '{symbol} için geçmiş veri yok; dışarıda bırakıldı.',
  'Backtests run for this strategy so far: {n}. The more variations you try, the more likely the best one is luck.': 'Bu strateji için şimdiye kadar yapılan test sayısı: {n}. Ne kadar çok deneme yaparsan, en iyi sonucun şans olma ihtimali o kadar artar.',
  Result: 'Sonuç',
  'worst fall {p}%': 'en büyük düşüş %{p}',
  'Per year': 'Yıllık',
  annualised: 'yıllığa çevrilmiş',
  'Worst fall from a peak': 'Zirveden en büyük düşüş',
  'longest losing streak: {n}': 'en uzun zarar serisi: {n}',
  'Account value: strategy vs. holding BTC': 'Hesap değeri: strateji ve BTC tutmak',
  'Compared with doing nothing clever': 'Hiçbir şey yapmamakla karşılaştırma',
  'This strategy': 'Bu strateji',
  'This strategy with 1.5× fees': 'Bu strateji, 1,5 kat komisyonla',
  'Avg win {w} · avg loss {l} · profit factor {pf} · Sharpe {s} · invested on average {x}% of the time': "Ort. kazanç {w} · ort. kayıp {l} · kâr faktörü {pf} · Sharpe {s} · paranın ortalama %{x}'i yatırımda",
  'Year by year': 'Yıl yıl',
  Period: 'Dönem',
  'Worst fall': 'En büyük düşüş',
  'A real edge should hold up in more than one kind of market.': 'Gerçek bir avantaj, birden fazla piyasa koşulunda işe yaramalı.',
  Bought: 'Alış',
  Sold: 'Satış',
  Invested: 'Yatırılan',
  'Show all {n}': 'Tümünü göster ({n})',
  'Test details': 'Test ayrıntıları',
  'Trend follower (daily)': 'Trend Takipçisi (günlük)',
  'Buys coins breaking out to new highs and holds while the trend lasts. Research-backed; tested over years.': 'Yeni zirveye çıkan coinleri alır ve trend sürdükçe tutar. Akademik araştırmalarla desteklenir; yıllara yayılan verilerle test edilir.',
  'Chart hunter (5 min)': 'Grafik Avcısı (5 dk)',
  'The live chart strategy: buys oversold dips on 5-minute bars. At most 60 days per test.': 'Canlıdaki grafik stratejisi: 5 dakikalık mumlarda aşırı düşüşleri alır. Test başına en fazla 60 gün.',
  'Result looks reliable': 'Sonuç güvenilir görünüyor',
  'Treat with caution': 'Dikkatle değerlendir',
  'Not enough evidence': 'Yeterli kanıt yok',
  'Only {value} trades — fewer than 30 prove nothing.': "Sadece {value} işlem — 30'dan az işlem hiçbir şey kanıtlamaz.",
  '{value} trades — at least 100 are needed to rely on it.': '{value} işlem — güvenmek için en az 100 işlem gerekir.',
  'The risk-adjusted return looks too good to be true.': 'Riske göre getiri gerçek olamayacak kadar iyi görünüyor.',
  'Win rate above 90% — suspiciously high.': "Kazanma oranı %90'ın üstünde — şüpheli derecede yüksek.",
  'Did not beat: {value}.': 'Şunu geçemedi: {value}.',
  'It lost money after fees — beating the comparisons below is not an edge.': 'Komisyonlardan sonra zarar etti — aşağıdaki karşılaştırmaları geçmesi bir avantaj sayılmaz.',
  'The profit disappears if fees and slippage are 1.5× higher.': 'Komisyon ve kayma 1,5 kat olursa kâr ortadan kalkıyor.',
  'Holding BTC': 'BTC tutmak',
  'Holding every coin equally': 'Tüm coinleri eşit tutmak',
  'Buying at random times': 'Rastgele zamanlarda almak',
  "Coins were picked from today's Binance list, so coins that were delisted since are missing. Real results would likely be somewhat worse.": 'Coinler bugünkü Binance listesinden seçildi; o zamandan beri listeden çıkarılan coinler testte yok. Gerçek sonuçlar muhtemelen biraz daha kötü olurdu.',
  'Live gates need order books and fresh tickers: the spread is charged as a fixed cost instead, and an enabled BTC check is approximated on the test’s own candles.': 'Canlı kapılar emir defteri ve taze fiyat ister: spread bunun yerine sabit maliyet olarak düşülür, açık bir BTC kontrolü ise testin kendi mumlarıyla yaklaşık olarak uygulanır.',
  'The Bitcoin check uses 1-minute candles, as live.': 'Bitcoin kontrolü canlıdaki gibi 1 dakikalık mumlarla yapılır.',
  'last 90 days': 'son 90 gün',
  'trend exit': 'trend bitti',
  'end of test': 'test sonu',
  queued: 'sırada',
  done: 'tamamlandı',
  // ── Coin list & new listings ──
  'Coin list settings saved — applied on the next ranking': 'Coin listesi ayarları kaydedildi — bir sonraki sıralamada uygulanır',
  'Binance has {n} USDT pairs; all are ranked every {min} min and the top {top} above {vol} USDT volume are traded.': "Binance'te {n} USDT paritesi var; hepsi {min} dakikada bir sıralanır ve hacmi {vol} USDT üstündeki ilk {top} coin işleme alınır.",
  'New listings on Binance': "Binance'te yeni listelenenler",
  'The full Binance market list is checked every {sec} seconds. A new USDT pair is announced on Telegram and watched for {days} days.': "Binance'in tüm coin listesi {sec} saniyede bir kontrol edilir. Yeni bir USDT paritesi Telegram'dan bildirilir ve {days} gün izlenir.",
  'Last check: {time}.': 'Son kontrol: {time}.',
  'No new listings in the last 30 days since tracking started.': 'Takip başladığından beri son 30 günde yeni listeleme yok.',
  'First seen': 'İlk görülme',
  'Trading since': 'İşlem başlangıcı',
  'Bot watching': 'Bot izliyor',
  Yes: 'Evet',
  No: 'Hayır',
  'New listing on Binance: {symbol} is now trading': "Binance'te yeni listeleme: {symbol} işleme açıldı",
  'Coming to Binance: {symbol}': "Binance'e geliyor: {symbol}",
  'Removed from Binance: {symbol}': "Binance'ten kaldırıldı: {symbol}",
  'How many coins to trade': 'Kaç coin işlem görsün',
  'The most traded USDT pairs on Binance, by 24h volume.': "Binance'te 24 saatlik hacmi en yüksek USDT pariteleri.",
  'Minimum 24h volume (USDT)': 'En düşük 24s hacim (USDT)',
  'Coins trading less than this are skipped: thin markets move erratically.': 'Hacmi bunun altındaki coinler atlanır: sığ piyasalar düzensiz hareket eder.',
  'Watch new listings for (days)': 'Yeni listelenenleri izleme süresi (gün)',
  'A coin Binance just opened is added regardless of volume. 0 = off.': "Binance'in yeni açtığı coin hacmine bakılmadan eklenir. 0 = kapalı.",
  'Re-rank every (min)': 'Yeniden sıralama aralığı (dk)',
  'How often the ranking is rebuilt.': 'Sıralamanın ne sıklıkla yenileneceği.',
  'Coming soon': 'Yakında',
  Trading: 'İşlemde',
  'Closing {symbol}': '{symbol} kapatılıyor',
  Trades: 'İşlemler',
  'All statuses': 'Tüm durumlar',
  '{n} trades': '{n} işlem',
  Status: 'Durum',
  Reason: 'Neden',
  Detail: 'Detay',
  'No trades.': 'İşlem yok.',
  Order: 'Emir',
  Purpose: 'Amaç',
  Side: 'Yön',
  Filled: 'Gerçekleşen',
  'Avg price': 'Ort. fiyat',
  Fee: 'Komisyon',
  'Signal reason': 'Sinyal gerekçesi',
  Signals: 'Sinyaller',
  All: 'Tümü',
  'Conf.': 'Güven',
  Gates: 'Filtreler',
  Why: 'Neden',
  'No signals yet.': 'Henüz sinyal yok.',
  // trade statuses
  opening: 'açılıyor',
  closing: 'kapanıyor',
  failed: 'başarısız',
  // signal statuses
  generated: 'üretildi',
  confirmed: 'onaylandı',
  rejected: 'reddedildi',
  executed: 'uygulandı',
  expired: 'süresi doldu',
  // close reasons
  tp: 'kâr al',
  sl: 'zarar kes',
  trail: 'iz süren stop',
  scale_out_final: 'kademeli çıkış (son)',
  manual: 'manuel',
  delisting: 'delist',
  // order purposes, sides, statuses
  entry: 'giriş',
  exit_tp: 'çıkış (kâr al)',
  exit_sl: 'çıkış (zarar kes)',
  exit_trail: 'çıkış (iz süren stop)',
  exit_scaleout: 'çıkış (kademeli)',
  exit_news: 'çıkış (haber)',
  buy: 'al',
  sell: 'sat',
  pending: 'bekliyor',
  submitted: 'gönderildi',
  partially_filled: 'kısmen gerçekleşti',
  filled: 'gerçekleşti',
  canceled: 'iptal',

  // ── Strategies ──
  sentiment_first: 'Haber Odaklı',
  hybrid_confirmation: 'Haber + Piyasa Onayı',
  technical_hunter: 'Grafik Avcısı',
  trailing_stop: 'Takip eden stop',
  fixed_tpsl: 'Sabit kâr al / zarar kes',
  scale_out: 'Kademeli çıkış',
  'Strong news sentiment triggers; the four news-reaction gates filter. Waits up to rvol.maxWaitSec for volume.':
    'Güçlü haber duygusu tetikler, dört haber tepki filtresi eler. Hacim için en fazla rvol.maxWaitSec kadar bekler.',
  'Sentiment opens a window; a volume breakout plus a bid-heavy order book must confirm inside it.':
    'Duygu bir onay penceresi açar; pencere içinde hacim kırılımı ve alış ağırlıklı emir defteri onaylamalıdır.',
  'No news: RSI oversold dip followed by a MACD turn. Optional BTC-regime and spread gates.':
    'Habersiz: RSI aşırı satım dibi ve ardından MACD dönüşü. BTC rejim ve spread filtreleri isteğe bağlı.',
  active: 'aktif',
  Activate: 'Aktifleştir',
  Save: 'Kaydet',
  Revert: 'Geri al',
  '{name} saved — applied without restart': '{name} kaydedildi — yeniden başlatmadan uygulandı',
  '{name} is now active': '{name} artık aktif',
  'Exit strategies': 'Çıkış stratejileri',
  default: 'varsayılan',
  'Make default': 'Varsayılan yap',
  '{name} is the default exit': '{name} varsayılan çıkış oldu',
  'Exit params saved': 'Çıkış parametreleri kaydedildi',
  'params must be valid JSON': 'parametreler geçerli JSON olmalı',

  // strategy parameter labels (SchemaForm: humanized keys)
  'Min composite score': 'Haber ne kadar olumlu olmalı',
  'Min composite confidence': 'Haberden ne kadar emin olunmalı',
  'Max signals per hour': 'Saatte en fazla alım',
  'Cooldown per symbol min': 'Aynı coini tekrar almadan önce bekleme (dk)',
  'RVOL — is the market reacting?': 'RVOL — piyasa tepki veriyor mu?',
  'Priced-in — are we too late?': 'Fiyatlanma — geç mi kaldık?',
  'BTC regime — is the backdrop OK?': 'BTC rejimi — genel piyasa uygun mu?',
  'Spread — is entry affordable?': 'Spread — giriş maliyeti makul mü?',
  Enabled: 'Açık',
  'Window bars': 'Son kaç dakikaya bakılsın (mum)',
  'Lookback bars': '“Normal” hacim için kaç dakikaya bakılsın (mum)',
  'Min rvol': 'İşlem hacmi en az kaç kat artmalı',
  'Max wait sec': 'Hacim artışı için en fazla bekleme (sn)',
  'Max move atr': 'Kaçırılmış hareket sınırı (tipik hareket sayısı)',
  'Max news age sec': 'Haberin en fazla yaşı (sn)',
  'Atr period': 'Tipik hareket ölçümü (mum sayısı)',
  'Lookback min': 'Bitcoin’in son kaç dakikasına bakılsın',
  'Max btc drop pct': 'Bitcoin en fazla düşüş (%)',
  'Max spread bps': 'Alış-satış fiyat farkı sınırı (bps)',
  'Max ticker age ms': 'Fiyat verisi en fazla ne kadar eski olabilir (ms)',
  'Confirmation window ms': 'Piyasa onayı için bekleme süresi (ms)',
  'Min orderbook imbalance': 'En az alıcı oranı (0–1)',
  'Max orderbook age ms': 'Emir defteri verisi en fazla ne kadar eski olabilir (ms)',
  Timeframe: 'Grafik mum süresi',
  'Rsi period': 'RSI periyodu',
  'Rsi oversold': 'Aşırı düşüş eşiği (RSI)',
  'Oversold lookback bars': 'Düşüş en fazla kaç mum önce olmuş olabilir',
  'Macd fast': 'MACD hızlı',
  'Macd slow': 'MACD yavaş',
  'Macd signal': 'MACD sinyal',
  'Volume profile bars': 'En çok işlem gören fiyatı bulma (mum sayısı)',

  // ── Strategies page (plain-language guide) ──
  '= {n} seconds': '= {n} saniye',
  '= {n} minutes': '= {n} dakika',
  '= {n} hours': '= {n} saat',
  'allowed: {min} – {max}': 'izin verilen: {min} – {max}',
  'What is a strategy?': 'Strateji nedir?',
  'A strategy decides when the bot BUYS a coin. When to SELL is decided separately by the exit strategy below. You can run several strategies at once — for example the chart strategy next to a news strategy — and compare them on the Reports page. A coin is never bought twice: whichever strategy buys it first holds it.':
    'Strateji, botun bir coini ne zaman ALACAĞINA karar verir. Ne zaman SATACAĞINA ise aşağıdaki çıkış stratejisi ayrıca karar verir. Birden fazla stratejiyi aynı anda çalıştırabilirsin — örneğin Grafik Avcısı ile bir haber stratejisini birlikte — ve Raporlar sayfasında hangisinin daha iyi olduğunu karşılaştırabilirsin. Bir coin iki kez alınmaz: hangi strateji önce alırsa pozisyon onundur.',
  'Right now the bot is in simulation: it trades with pretend money, so trying strategies costs nothing.':
    'Bot şu an simülasyonda: sanal parayla işlem yapıyor, yani stratejileri denemek sana hiçbir şeye mal olmaz.',
  'The bot is LIVE: strategies trade real money.': 'Bot CANLI: stratejiler gerçek parayla işlem yapıyor.',
  'How it works': 'Nasıl çalışır',
  'Good for': 'İşe yaradığı durum',
  'Watch out': 'Dikkat',
  'Main settings': 'Ana ayarlar',
  'Advanced settings': 'Gelişmiş ayarlar',
  'Every setting has a safe default. You only need to touch the main settings — or none at all.':
    'Her ayarın güvenli bir varsayılan değeri var. Sadece ana ayarlarla oynaman yeterli — hiç dokunmasan da olur.',
  'After buying, the exit strategy decides when to sell. The one marked “in use” applies to every new trade.':
    'Alımdan sonra ne zaman satılacağına çıkış stratejisi karar verir. “Kullanılıyor” yazan, her yeni işleme uygulanır.',
  'in use': 'kullanılıyor',
  'Use for new trades': 'Yeni işlemlerde kullan',
  'Uses news': 'Haberlere bakar',
  'Uses news + market': 'Haber + piyasaya bakar',
  'Uses charts only': 'Sadece grafiğe bakar',
  'Trades: sometimes': 'İşlem sıklığı: ara sıra',
  'Trades: rarely': 'İşlem sıklığı: seyrek',
  'Trades: often': 'İşlem sıklığı: sık',
  Active: 'Aktif',
  'Turn on': 'Çalıştır',
  'Stop this strategy': 'Durdur',
  '{name} stopped': '{name} durduruldu',
  // guide: Haber Odaklı
  'Buys when the news about a coin is clearly good — but only if the market is actually reacting to it.':
    'Bir coin hakkındaki haberler açıkça iyiyse alım yapar — ama sadece piyasa da gerçekten tepki veriyorsa.',
  'Every minute it reads crypto news sites and Binance announcements and rates each headline as good or bad for the coin it mentions.':
    'Her dakika kripto haber sitelerini ve Binance duyurularını okur; her başlığı, bahsettiği coin için iyi mi kötü mü diye puanlar.',
  'When a coin’s news is clearly good, it checks the market: is trading suddenly busier than normal, has the price not already jumped, is Bitcoin calm, is buying cheap right now?':
    'Bir coinin haberi açıkça iyiyse piyasayı kontrol eder: işlemler normalden birden yoğunlaştı mı, fiyat zaten fırlamadı mı, Bitcoin sakin mi, şu an almak pahalı değil mi?',
  'If every check passes it buys. If trading hasn’t picked up yet it waits up to 3 minutes, then gives up.':
    'Tüm kontroller geçerse alır. İşlemler henüz yoğunlaşmadıysa en fazla 3 dakika bekler, olmazsa vazgeçer.',
  'Days when big news moves prices. The fastest of the three to react.': 'Büyük haberlerin fiyatı oynattığı günler. Üçü içinde en hızlı tepki veren.',
  'News can be misread or already known by everyone; the market checks exist to catch that.':
    'Haber yanlış yorumlanabilir ya da zaten herkes tarafından biliniyor olabilir; piyasa kontrolleri tam da bunu yakalamak için var.',
  // guide: Haber + Piyasa Onayı
  'Waits for good news AND a clear sign from the market before buying — slower, but pickier.':
    'Almadan önce hem iyi haber HEM de piyasadan net bir işaret bekler — daha yavaş ama daha seçici.',
  'Good news about a coin opens a watch window (5 minutes by default).': 'Bir coin hakkında iyi haber gelince bir izleme penceresi açılır (varsayılan 5 dakika).',
  'Inside that window it waits until trading gets busier than normal and there are clearly more buyers than sellers.':
    'Bu pencerede işlemlerin normalden yoğunlaşmasını ve alıcıların satıcılardan açıkça fazla olmasını bekler.',
  'Only then — if the price hasn’t already run away and Bitcoin is calm — it buys. If the window ends first, nothing happens.':
    'Ancak o zaman — fiyat çoktan kaçmamışsa ve Bitcoin sakinse — alır. Pencere önce kapanırsa hiçbir şey olmaz.',
  'Starting out: fewer trades, each one confirmed twice.': 'Yeni başlarken: daha az işlem, her biri iki kez onaylanmış.',
  'Being picky means it misses some fast moves.': 'Seçici olduğu için bazı hızlı hareketleri kaçırır.',
  // guide: Grafik Avcısı
  'Ignores the news. Buys a coin that has dropped sharply at the moment its chart shows the fall turning around.':
    'Haberlere bakmaz. Sert düşmüş bir coini, grafiği düşüşün döndüğünü gösterdiği anda alır.',
  'It watches the price chart of every coin in the list.': 'Listedeki her coinin fiyat grafiğini izler.',
  'It looks for a coin that fell hard in a short time (“oversold”).': 'Kısa sürede sert düşmüş (“aşırı satılmış”) bir coin arar.',
  'When the chart shows that fall starting to turn upward, it buys — expecting a bounce.': 'Grafik bu düşüşün yukarı dönmeye başladığını gösterince alır — bir toparlanma bekleyerek.',
  'Markets that swing up and down without big news.': 'Büyük haber olmadan inip çıkan piyasalar.',
  'In a strong downtrend a falling coin can keep falling; the exit strategy limits the loss.': 'Güçlü bir düşüş döneminde düşen coin düşmeye devam edebilir; çıkış stratejisi zararı sınırlar.',
  // exit guides
  'Follows the price up and sells if it falls a set % below the highest point. Lets winning trades run and cuts them when the trend turns; once it protects a profit, that profit is real after fees.':
    'Fiyat yükseldikçe onu takip eder; en yüksek noktadan belirli bir % düşerse satar. Kazanan işlemi koşturur, gidiş dönünce keser; kâr korumaya başladığında bu kâr komisyonlar düşüldükten sonra da gerçektir.',
  'Sells at a fixed profit target or a fixed loss limit, whichever comes first — both counted after Binance fees. Simple and predictable.':
    'Sabit bir kâr hedefinde ya da sabit bir zarar sınırında satar, hangisi önce gelirse — ikisi de Binance komisyonları düşülerek hesaplanır. Basit ve öngörülebilir.',
  'Sells part of the position at a first profit target, then follows the rest like the trailing stop.':
    'İlk kâr hedefinde pozisyonun bir kısmını satar, kalanını takip eden stop gibi izler.',
  // parameter help
  'How good the news must be: −1 very bad, 0 neutral, 1 very good. 0.5 means clearly good.': 'Haber ne kadar iyi olmalı: −1 çok kötü, 0 nötr, 1 çok iyi. 0,5 “açıkça iyi” demek.',
  'How sure the news reading must be, from 0 to 1. Higher means fewer but surer trades.': 'Haber yorumundan ne kadar emin olunmalı (0–1). Yüksek değer = daha az ama daha emin işlem.',
  'At most this many buys per hour across all coins — a brake when a lot of news arrives at once.': 'Tüm coinlerde saatte en fazla bu kadar alım — aynı anda çok haber gelirse fren görevi görür.',
  'After buying a coin, wait this many minutes before buying the same coin again.': 'Bir coini aldıktan sonra aynı coini tekrar almadan önce bu kadar dakika bekle.',
  'How long to wait for the market to confirm the news.': 'Piyasanın haberi onaylaması için ne kadar beklensin.',
  'Share of buyers among waiting orders: 0.6 means at least 60% buyers.': 'Bekleyen emirlerde alıcıların payı: 0,6 = en az %60 alıcı.',
  'Order-book data older than this is not trusted.': 'Bundan eski emir defteri verisine güvenilmez.',
  'Chart candle size: 1 minute or 5 minutes. 5 minutes is calmer.': 'Grafik mum süresi: 1 dakika ya da 5 dakika. 5 dakika daha sakindir.',
  'How many candles the “oversold” meter (RSI) looks at. 14 is standard.': '“Aşırı düşüş” ölçerinin (RSI) baktığı mum sayısı. Standart 14.',
  'RSI below this counts as oversold — a sharp drop. 30 is standard; lower means only very sharp drops.': 'RSI bunun altına inerse sert düşüş sayılır. Standart 30; daha düşük değer = sadece çok sert düşüşler.',
  'The drop must have happened within this many candles before the turn.': 'Düşüş, dönüşten en fazla bu kadar mum önce olmuş olmalı.',
  'Part of the turn detector (MACD). Standard values: 12 / 26 / 9.': 'Dönüş dedektörünün (MACD) bir parçası. Standart değerler: 12 / 26 / 9.',
  'How many candles are used to measure a coin’s typical move.': 'Bir coinin tipik hareketini ölçmek için kaç mum kullanılsın.',
  'How many candles are used to find the price where most trading happened.': 'En çok işlem gören fiyatı bulmak için kaç mum kullanılsın.',
  'Checks done on the market right before every news-based buy. Each can be turned off.': 'Habere dayalı her alımdan hemen önce piyasada yapılan kontroller. Her biri kapatılabilir.',
  'Turn this check on or off.': 'Bu kontrolü aç ya da kapat.',
  'How many of the latest 1-minute candles are compared with normal.': 'Son kaç 1 dakikalık mum “normal” ile karşılaştırılsın.',
  'How many earlier 1-minute candles define “normal” trading.': '“Normal” işlem yoğunluğunu kaç önceki 1 dakikalık mum belirlesin.',
  'Trading must be at least this many times busier than normal (2 = twice as busy).': 'İşlemler normalden en az bu kadar kat yoğun olmalı (2 = iki kat).',
  'How long to wait after the news for trading to pick up.': 'Haberden sonra işlemlerin yoğunlaşması için ne kadar beklensin.',
  'Skip if the price already moved more than this many typical moves since the news.': 'Fiyat haberden beri bu kadar tipik hareketten fazla oynadıysa alma (tren kaçmış).',
  'Ignore news older than this.': 'Bundan eski haberleri yok say.',
  'How many minutes of Bitcoin’s price to look at.': 'Bitcoin fiyatının son kaç dakikasına bakılsın.',
  'Skip if Bitcoin fell more than this % in that time.': 'Bitcoin bu sürede bu %’den fazla düştüyse alma.',
  'Skip if the gap between buy and sell prices is wider than this (100 bps = 1%).': 'Alış ve satış fiyatı arasındaki fark bundan büyükse alma (100 bps = %1).',
  'Price data older than this is treated as stale.': 'Bundan eski fiyat verisi bayat sayılır.',
  'Sell if the price falls this % below its highest point since buying.': 'Fiyat, alımdan beri gördüğü en yüksek noktanın bu % altına düşerse sat.',
  'Optional: give the stop more room in wild markets (in typical moves). Leave empty to turn off.': 'İsteğe bağlı: çok dalgalı piyasada stopa daha fazla alan tanı (tipik hareket cinsinden). Kapatmak için boş bırak.',
  'Start following the price only after it is this % above the buy price (0 = right away).': 'Fiyatı takip etmeye ancak alış fiyatının bu % üstüne çıkınca başla (0 = hemen).',
  'Take profit: sell when the trade is up this % after both Binance fees.': 'Kâr al: işlem, iki Binance komisyonu düşüldükten sonra bu % kârdayken sat.',
  'Stop loss: sell when the trade is down this %, fees included.': 'Zarar kes: işlem, komisyonlar dahil bu % zarardayken sat.',
  'First target: sell part of the position when the trade is up this % after fees.': 'İlk hedef: işlem, komisyonlar düşüldükten sonra bu % kârdayken pozisyonun bir kısmını sat.',
  'How much of the position to sell at the first target, in %.': 'İlk hedefte pozisyonun yüzde kaçı satılsın.',
  // exit parameter labels
  'Trail pct': 'Takip mesafesi (%)',
  'Ratchet atr mult': 'Dalgalı piyasada ek pay (tipik hareket)',
  'Activation pct': 'Takibe başlama kârı (%)',
  'Tp pct': 'Kâr al (%)',
  'Sl pct': 'Zarar kes (%)',
  'Tp1 pct': 'İlk hedef (%)',
  'Tp1 size pct': 'İlk hedefte satılacak kısım (%)',

  // ── Reports ──
  Reports: 'Raporlar',
  Net: 'Net',
  Gain: 'Kazanç',
  Loss: 'Kayıp',
  Fees: 'Komisyon',
  '{n} trades · {w} won · {l} lost': '{n} işlem · {w} kazançlı · {l} zararlı',
  'Net profit or loss per period': 'Dönem başına net kâr/zarar',
  'Net profit or loss per coin': 'Coin başına net kâr/zarar',
  'Date range': 'Tarih aralığı',
  From: 'Başlangıç',
  To: 'Bitiş',
  'Group charts by': 'Grafikleri grupla',
  Account: 'Hesap',
  'Total gained': 'Toplam kazanç',
  '{n} winning trades': '{n} kazançlı işlem',
  'Total lost': 'Toplam kayıp',
  '{n} losing trades': '{n} zararlı işlem',
  'Net result': 'Net sonuç',
  'fees included': 'komisyonlar dahil',
  'Fees paid': 'Ödenen komisyon',
  'win rate {p}%': 'kazanma oranı %{p}',
  'Bought / sold total': 'Alış / satış toplamı',
  'Net result per {period}': '{period} bazında net sonuç',
  'week of {date}': '{date} haftası',
  'Which coin earned or lost how much': 'Hangi coinden ne kadar kazandık / kaybettik',
  'Closed trades': 'Kapanan işlemler',
  'newest 500 shown; totals include all': 'en yeni 500 gösteriliyor; toplamlar hepsini kapsar',
  'Download CSV': 'CSV indir',
  Coin: 'Coin',
  Closed: 'Kapanış',
  'Bought for': 'Alış tutarı',
  'Sold for': 'Satış tutarı',
  'Profit / loss': 'Kâr / zarar',
  'Balance at buy': 'Alışta toplam bakiye',
  'Balance at sell': 'Satışta toplam bakiye',
  'This week': 'Bu hafta',
  'This month': 'Bu ay',
  'Last 30 days': 'Son 30 gün',
  'Last 90 days': 'Son 90 gün',
  'All time': 'Tümü',
  Daily: 'Günlük',
  Weekly: 'Haftalık',
  Monthly: 'Aylık',
  day: 'gün',
  week: 'hafta',
  month: 'ay',
  'Buy price': 'Alış fiyatı',
  'Sell price': 'Satış fiyatı',
  'Bought for (USDT)': 'Alış tutarı (USDT)',
  'Sold for (USDT)': 'Satış tutarı (USDT)',
  'Fees (USDT)': 'Komisyon (USDT)',
  'Profit / loss (USDT)': 'Kâr / zarar (USDT)',
  '%': '%',

  // ── Risk & universe ──
  Risk: 'Risk',
  'Max concurrent trades': 'Aynı anda en fazla işlem',
  'Max daily drawdown %': 'Günlük en fazla düşüş %',
  'Base position % of equity': 'Temel pozisyon (varlığın %)',
  'Max position %': 'En büyük pozisyon %',
  'Min confidence (0–1)': 'Min. güven (0–1)',
  'Cooldown after loss (min)': 'Zarardan sonra bekleme (dk)',
  'Risk settings saved': 'Risk ayarları kaydedildi',
  'Symbol vetoes': 'Sembol vetoları',
  'Set automatically by Binance delisting announcements. They never expire on their own.':
    'Binance delist duyurularıyla otomatik konur. Kendiliğinden kalkmaz.',
  'No vetoes.': 'Veto yok.',
  Clear: 'Kaldır',
  'Veto on {symbol} cleared': '{symbol} üzerindeki veto kaldırıldı',
  'Asset universe': 'Coin listesi',
  Whitelist: 'Beyaz liste',
  Blacklist: 'Kara liste',
  whitelist: 'beyaz liste',
  blacklist: 'kara liste',
  '{symbol} added to {list}': '{symbol}, {list} listesine eklendi',
  '{symbol} removed': '{symbol} çıkarıldı',
  'Rescan now': 'Şimdi tara',
  'Universe rescanned': 'Coin listesi yeniden tarandı',
  'Top {n} USDT pairs above {vol} USDT 24h volume, refreshed every {min} min.':
    '24 saatlik hacmi {vol} USDT üzerindeki ilk {n} USDT paritesi; {min} dakikada bir yenilenir.',
  'Active ({n}):': 'Aktif ({n}):',

  // ── News ──
  'not polled': 'henüz çekilmedi',
  healthy: 'sağlıklı',
  failing: 'hatalı',
  'RSS feeds': 'RSS akışları',
  'Binance announcements': 'Binance duyuruları',
  '{n} new today · last poll {time}': 'bugün {n} yeni · son çekim {time}',
  enabled: 'açık',
  'Source enabled': 'Kaynak açıldı',
  'Source disabled': 'Kaynak kapatıldı',
  'Test fetch': 'Deneme çekimi',
  'Poll interval (s)': 'Çekim aralığı (sn)',
  'Interval saved': 'Aralık kaydedildi',
  '{name} on': '{name} açıldı',
  '{name} off': '{name} kapatıldı',
  'On a delisting of a coin we hold': 'Elimizdeki bir coin delist edilirse',
  'Delisting action saved': 'Delist davranışı kaydedildi',
  'close the position at market': 'pozisyonu piyasadan kapat',
  'block entries only, keep the position': 'sadece yeni alımı engelle, pozisyonu tut',
  '— failed': '— başarısız',
  'Symbol map': 'Sembol eşleme',
  'Name → ticker aliases, and tickers that are also common words (matched only as $CASHTAG or via an alias).':
    'İsim → ticker eşlemeleri ve aynı zamanda sıradan kelime olan tickerlar (yalnızca $CASHTAG veya eşleme ile yakalanır).',
  'Symbol map saved': 'Sembol eşleme kaydedildi',
  'not valid JSON': 'geçerli JSON değil',
  'Recent news': 'Son haberler',
  Published: 'Yayın',
  Source: 'Kaynak',
  Category: 'Kategori',
  Symbols: 'Semboller',
  Title: 'Başlık',
  '(duplicate)': '(tekrar)',
  general: 'genel',
  listing: 'listeleme',
  exchange_notice: 'borsa duyurusu',

  // ── Sentiment ──
  'Saved — applied without restart': 'Kaydedildi — yeniden başlatmadan uygulandı',
  Adapter: 'Analizci',
  Weight: 'Ağırlık',
  Health: 'Durum',
  'Calls today': 'Bugünkü çağrı',
  'Cost today': 'Bugünkü maliyet',
  'Per call': 'Çağrı başına',
  'needs key (paid)': 'anahtar gerekir (ücretli)',
  ok: 'sorunsuz',
  free: 'ücretsiz',
  Playground: 'Deneme alanı',
  Score: 'Skor',
  score: 'skor',
  confidence: 'güven',
  'No opinion — no rule matched this headline.': 'Görüş yok — bu başlığa uyan kural bulunamadı.',
  'Recent scores': 'Son skorlar',
  Headline: 'Başlık',
  'No scores yet.': 'Henüz skor yok.',
  bullish: 'yükseliş',
  bearish: 'düşüş',
  neutral: 'nötr',

  // ── Credentials ──
  set: 'kayıtlı',
  'not set': 'kayıtlı değil',
  'verified {time}': 'doğrulandı {time}',
  'Spot trading permission only — NO withdrawal permission — IP-restricted to the VPS. Needed only for live mode.':
    'Yalnızca spot işlem izni — para çekme izni YOK — IP kısıtlaması VPS’e. Sadece canlı mod için gerekir.',
  'Bot token from @BotFather. Commands are accepted only from TELEGRAM_ALLOWED_CHAT_ID.':
    '@BotFather’dan alınan bot token’ı. Komutlar yalnızca TELEGRAM_ALLOWED_CHAT_ID’den kabul edilir.',
  'Optional and paid per call. The bot runs on the free regex engine without it.':
    'İsteğe bağlı ve çağrı başına ücretli. Bot bunsuz, ücretsiz regex motoruyla çalışır.',
  'bot token': 'bot token',
  'API key': 'API anahtarı',
  secret: 'gizli anahtar',
  '{provider} saved (encrypted)': '{provider} kaydedildi (şifreli)',
  Verify: 'Doğrula',
  'Verify failed: {detail}': 'Doğrulama başarısız: {detail}',
  Remove: 'Sil',
  'Remove the {provider} credential?': '{provider} anahtarı silinsin mi?',
  Removed: 'Silindi',
  'Anything depending on it stops working until a new one is saved.': 'Yenisi kaydedilene kadar buna bağlı her şey durur.',
  'Stored AES-256-GCM encrypted. The server never sends a key back — only its last four characters.':
    'AES-256-GCM ile şifreli saklanır. Sunucu anahtarı asla geri göndermez — sadece son dört karakterini.',

  // ── Settings & audit ──
  paper_wallet: 'Simülasyon cüzdanı',
  market_data: 'Piyasa verisi',
  notifications: 'Bildirimler',
  sentiment: 'Duygu analizi',
  universe: 'Coin listesi',
  '{name} saved': '{name} kaydedildi',
  'Change password': 'Şifre değiştir',
  'current password': 'mevcut şifre',
  'new password (min 12 characters)': 'yeni şifre (en az 12 karakter)',
  'Password changed': 'Şifre değiştirildi',
  Change: 'Değiştir',
  'Audit log': 'Denetim kaydı',
  Actor: 'Kim',
  Action: 'İşlem',
  Entity: 'Kayıt',
  user: 'kullanıcı',
  telegram: 'telegram',
  engine: 'motor',
  system: 'sistem',
  // ── Phase 7 (generic) ──
  time: 'süre doldu',
  'Max hold min': 'En uzun tutma süresi (dk)',
  'The live chart strategy: buys oversold dips on 5-minute bars. Up to 180 days, coins × days at most 1800.': 'Canlıdaki grafik stratejisi: 5 dakikalık mumlarda aşırı satılmış düşüşleri alır. En fazla 180 gün, coin × gün en fazla 1800.',
  'The Bitcoin check uses 5-minute candles here; live it uses 1-minute ones.': 'Bitcoin kontrolü burada 5 dakikalık mumlarla yapılır; canlıda 1 dakikalık mumlarla.',
  'Spread (bps)': 'Spread (bps)',
  'Gap between buy and sell price, paid half on each side. 10 bps = 0.1%.': 'Alış ile satış fiyatı arasındaki fark; yarısı alışta, yarısı satışta ödenir. 10 bps = %0,1.',
  Best: 'En iyi',
  Worst: 'En kötü',
  'Engine not running': 'Motor çalışmıyor',
  'Daily loss limit reached': 'Günlük zarar sınırına ulaşıldı',
  'Confidence too low': 'Güven çok düşük',
  'Coin not in the list': 'Coin listede değil',
  'Coin blocked (delisting)': 'Coin engelli (delist)',
  'All trade slots taken': 'Tüm işlem yerleri dolu',
  'Strategy’s own slots taken': 'Stratejinin kendi yerleri dolu',
  'Coin already held': 'Coin zaten elde',
  'Strategy’s capital share used up': 'Stratejinin sermaye payı doldu',
  'Waiting after a loss on this coin': 'Bu coindeki zarardan sonra bekleniyor',
  'Order below the exchange minimum': 'Emir borsanın alt sınırının altında',
  'No price available': 'Fiyat alınamadı',
  'Internal error': 'İç hata',
  'Check failed: trading not busy enough': 'Kontrol: işlem hacmi yetersiz',
  'Check failed: price already moved': 'Kontrol: fiyat zaten hareket etmiş',
  'Check failed: Bitcoin falling': 'Kontrol: Bitcoin düşüyor',
  'Check failed: buying too expensive (spread)': 'Kontrol: almak pahalı (spread)',
  'Why no trades? (7 days)': 'Neden işlem açmıyor? (7 gün)',
  'Signals the bot found but did not trade, by reason.': 'Botun bulduğu ama işleme dönüştürmediği sinyaller, nedene göre.',
  'No rejected signals in the last 7 days.': 'Son 7 günde reddedilen sinyal yok.',
  'Trade quality': 'İşlem kalitesi',
  Calculate: 'Hesapla',
  'For the latest 50 closed trades: how far the price rose (best) and fell (worst) while the trade was open, and where it was 2 hours after selling. A low “best” everywhere means bad buys; a high “best” on losing trades means the sell rule gave it back.': 'Son 50 kapanan işlem için: işlem açıkken fiyatın ne kadar yükseldiği (en iyi) ve düştüğü (en kötü), satıştan 2 saat sonra nerede olduğu. Her yerde düşük bir “en iyi” kötü alımlar demektir; zararlı işlemlerde yüksek bir “en iyi” ise kazancı satış kuralının geri verdiğini gösterir.',
  'Calculating…': 'Hesaplanıyor…',
  'Avg result': 'Ort. sonuç',
  'Avg best': 'Ort. en iyi',
  'Avg worst': 'Ort. en kötü',
  '2 h after selling': 'Satıştan 2 sa sonra',
  'Max open trades': 'En fazla açık işlem',
  'Max open trades saved': 'En fazla açık işlem kaydedildi',
  'At most this many open trades for this strategy, on top of the overall limit on the risk page — so a slow strategy cannot take every place.': 'Bu strateji için en fazla bu kadar açık işlem; risk sayfasındaki genel sınıra ek olarak — böylece yavaş bir strateji tüm yerleri kapatamaz.',
  'Sell after this many minutes if no stop or target was hit yet. Empty = no time limit.': 'Bu kadar dakika içinde stop ya da hedef gerçekleşmezse sat. Boş = süre sınırı yok.',
};
